import { getApplicationConfig, PubSubSharedActions, urls } from "@selector/shared";
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from "@xyflow/react";
import { nanoid } from "nanoid";
import PubSub from "pubsub-js";
import { storage } from "widgets";
import type { StateCreator } from "zustand";

import { notify } from "@/modules/notifications/notify.tsx";
import { isWorkflowRunning } from "@/modules/workflow/hooks/workflow.ts";
import type {
  WorkflowCopyPasteLocalStorageData,
  WorkflowCopyPasteSlice,
  WorkflowCopyPasteState,
  WorkflowEditorStore,
} from "@/modules/workflow/stores/workflow-editor-store-types";
import { save } from "@/modules/workflow/utils/debounceSave.ts";
import {
  type AutoSaveData,
  generateWorkflowConfig,
  getWorkflowEventInputForSaving,
} from "@/modules/workflow/utils/generateWorkflow.ts";
import { workflowParamToActivityInstanceParam } from "@/modules/workflow/utils/parameters.ts";
import { canAnyActivityUseSampleType, getSampleTypes } from "@/modules/workflow/utils/sample-type.ts";
import { type EdgeData, type EditorNodeData, LOCAL_STORAGE_KEY } from "@/modules/workflow/utils/types.ts";

export const initialCopyPasteSliceState: WorkflowCopyPasteState = {
  copyPasteSessionData: null,
};

const TOKEN_PREFIX = "SELECTOR_WORKFLOW_EDITOR-";

export const createCopyPasteSlice: StateCreator<WorkflowEditorStore, [], [], WorkflowCopyPasteSlice> = (set, get) => {
  PubSub.subscribe(PubSubSharedActions.LOGOUT, () => {
    delete storage[LOCAL_STORAGE_KEY];
    set(initialCopyPasteSliceState);
  });

  return {
    ...initialCopyPasteSliceState,
    async copy(nodeIds, cut = false) {
      // Save on the Store. This layer saved directly the parameters with their values because it is always in the same session.
      // And so same user, same project. If you log you out, data are wiped.

      if (!Array.isArray(nodeIds) || nodeIds.length === 0) {
        return;
      }

      const copyToken = `${TOKEN_PREFIX}${nanoid()}`;

      const nodes: Node<EditorNodeData>[] = get()
        .nodes.filter((node) => nodeIds.includes(node.id))
        .map((node) => ({ ...node, data: { ...node.data, outputFolderId: undefined, status: undefined } }));
      const connectableEdges = get().edges.filter(
        (edge) => nodes.some((node) => node.id === edge.source) || nodes.some((node) => node.id === edge.target),
      );
      const InBetweenEdges = get().edges.filter(
        (edge) => nodes.some((node) => node.id === edge.source) && nodes.some((node) => node.id === edge.target),
      );
      const parameters = Object.fromEntries(
        Object.entries(get().parametersRecord).filter(([nodeId, _]) => nodeIds.includes(nodeId)),
      );

      set({ copyPasteSessionData: { copyToken, nodes, edges: connectableEdges, parameters } });

      if (cut) {
        const onNodesChange = get().onNodesChange;
        get().onDelete({ nodes: nodes, edges: InBetweenEdges }); // To trigger the pushTimelineData
        for (const nodeId of nodeIds) {
          onNodesChange([
            {
              id: String(nodeId),
              type: "remove",
            },
          ]);
        }
      }

      const encryptedCopyToken = btoa(copyToken);

      if (window.isSecureContext && navigator.clipboard) {
        navigator.clipboard.writeText(encryptedCopyToken).catch((error) => {
          if (import.meta.env.DEV) {
            console.error("Copyable Error:", error.name, error.message);
          }
        });
      } else {
        // Unsecure environment. Workaround to copy to clipboard
        const input = document.createElement("input");
        input.style.position = "fixed";
        input.style.opacity = "0";
        input.value = encryptedCopyToken;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }

      // We don't support cross browser, cross server copy/paste.
      const config = generateWorkflowConfig(nodes, InBetweenEdges, parameters, get().sampleTypeId, undefined, true);

      const minX = Math.min(...config.activities.map((activity) => activity.metadata.position.x));
      const minY = Math.min(...config.activities.map((activity) => activity.metadata.position.y));

      const applicationConfiguration = await getApplicationConfig();
      const copyData: WorkflowCopyPasteLocalStorageData = {
        location: (applicationConfiguration?.serverHost ?? "") + urls.basePath,
        copyToken,
        activities: config.activities.map((activity) => ({
          ...activity,
          metadata: {
            width: activity.metadata.width,
            position: {
              x: activity.metadata.position.x - minX,
              y: activity.metadata.position.y - minY,
            },
          },
          parameters: activity.parameters.map((activityParam) => ({
            ...activityParam,
            typeName: get()
              .getParameters(activity.identifier)
              .find((param) => param.parameterId === activityParam.identifier)?.typeName,
          })),
        })),
        connectors: config.connectors,
        sampleTypeId: config.sampleTypeId,
      };

      storage[LOCAL_STORAGE_KEY] = btoa(JSON.stringify(copyData));
    },
    async paste() {
      if (isWorkflowRunning(get().workflowEvent.extendedExecutionStatus)) {
        return;
      }

      let copyToken = "";

      try {
        // clipboard is undefined in unsecure environments
        if (navigator.clipboard?.readText) {
          copyToken = atob(await navigator.clipboard.readText());
        } else {
          // Clipboard API is not supported by Firefox 115 ESR. document.execCommand('paste') neither - deprecated anyways.
          // The only other easy way to access clipboard would be with a "paste" event listener, but it's not possible to trigger a paste event programmatically.
          // So we have to ask the users having an outdated Firefox version to paste the code manually. It allows to not refactor everything for the very few - if none - of our users.
          // Version 128.0esr released on July 9, 2024, supports navigator.clipboard.readText().
          const pasteData = prompt("Please paste the code that was generated and copied to the clipboard.");

          if (pasteData != undefined) {
            copyToken = atob(pasteData);
          }
        }
      } catch (error) {
        // We may have atob not being able to decode the string in the clipboard. We don't react to that.
        if (error instanceof DOMException && error.name === "NotAllowedError") {
          notify.warning({
            title: "Paste Disabled",
            children: "Please enable access to clipboard in your browser settings.",
          });
        }
        return;
      }

      if (!copyToken?.startsWith(TOKEN_PREFIX)) {
        return;
      }

      const data = get().copyPasteSessionData;
      const localStorageData: WorkflowCopyPasteLocalStorageData | null = storage[LOCAL_STORAGE_KEY]
        ? JSON.parse(atob(storage[LOCAL_STORAGE_KEY]))
        : null;

      // First save if we have existing activities using the sample type as it might be
      // that the sample type is set but unused
      const existingWorkflowContainsTrackerActivities = canAnyActivityUseSampleType(get().getParameters());
      const applicationConfiguration = await getApplicationConfig();

      if (data != undefined && copyToken === data?.copyToken) {
        // We should be in the same session because of the presence of the data in the store

        // Deselect all nodes/edges because we only want the pasted ones selected
        get().deselectAll();

        // Create a copy of nodes using a new node id, preserving the initial ID to resurrect the edges between
        const nodeMap = new Map<string, string>(data.nodes.map((node) => [node.id, nanoid()]));
        const newNodes = data.nodes.map((node) => ({
          ...node,
          id: nodeMap.get(node.id)!,
          type: "activity",
          selected: true,
          position: {
            x: node.position.x + 100,
            y: node.position.y + 100,
          },
        }));
        const addNodeChanges: NodeChange[] = newNodes.map((item) => ({ type: "add", item }));

        // Resurrect existing connections between copied activities as well as ingoing/outgoing connections
        const addEdgeChanges: EdgeChange[] = data.edges
          .map((edge) => {
            const sourceExists = nodeMap.has(edge.source);
            const targetExists = nodeMap.has(edge.target);

            const isSourceAvailable = get().nodes.some((node) => node.id === edge.source);
            const isTargetAvailable = get().nodes.some((node) => node.id === edge.target);

            if (
              (sourceExists && targetExists) ||
              (sourceExists && isTargetAvailable) ||
              (targetExists && isSourceAvailable)
            )
              return {
                type: "add",
                item: {
                  ...edge,
                  id: nanoid(), // because we need to. Usually managed by reactflow. We don't care much what it is.
                  source: nodeMap.get(edge.source) ?? edge.source,
                  target: nodeMap.get(edge.target) ?? edge.target,
                  type: "activity",
                  selected: true,
                },
              } as EdgeChange;

            return null;
          })
          .filter((change): change is EdgeChange => change != undefined);

        const newNodesChanged = applyNodeChanges(addNodeChanges, get().nodes) as Node<EditorNodeData>[];
        const newEdgesChanged = applyEdgeChanges(addEdgeChanges, get().edges) as Edge<EdgeData>[];

        set({
          nodes: newNodesChanged,
          edges: newEdgesChanged,
        });

        get().pushTimelineData({
          nodes: newNodesChanged,
          edges: newEdgesChanged,
          parameters: [],
        });

        // Register the parameters for the "new" node (since it has a new id) without parameter validation
        for (const [oldNodeId, params] of Object.entries(data.parameters)) {
          const newNodeId = nodeMap.get(oldNodeId);
          if (!newNodeId) {
            continue;
          }
          const paramsFlat = params.flatMap((p) => workflowParamToActivityInstanceParam(p));
          await get().registerActivityParameters(newNodeId, paramsFlat);
        }
      } else if (
        localStorageData != undefined &&
        localStorageData.location === (applicationConfiguration?.serverHost ?? "") + urls.basePath &&
        copyToken === localStorageData?.copyToken
      ) {
        // You're in another sessions or browser tab
        const { activities, connectors, sampleTypeId } = localStorageData;

        const saveData: AutoSaveData = [
          get().workflowEvent,
          get().nodes,
          get().edges,
          get().parametersRecord,
          get().sampleTypeId,
        ];
        const currentSaveData = getWorkflowEventInputForSaving(saveData);
        const previousConfig = currentSaveData.config;
        const nodeMap = new Map<string, string>(activities.map((activity) => [activity.identifier, nanoid()]));
        const currentViewport = get().instance?.getViewport();
        const projectId = get().workflowEvent.projectId;

        if (
          projectId != undefined &&
          sampleTypeId != undefined &&
          (get().sampleTypeId == undefined ||
            (get().sampleTypeId != undefined && !existingWorkflowContainsTrackerActivities))
        ) {
          // We set the sample type, if it doesn't have been set yet.
          // If the sample type if not set, the tracker parameter values won't be set neither on paste.
          try {
            const sampleTypes = await getSampleTypes(projectId);
            if (sampleTypes.some((st) => st.id === sampleTypeId)) {
              currentSaveData.config.sampleTypeId = sampleTypeId;
            } else {
              notify.warning({
                title: "Paste Warning",
                children: "Sample type not found for this project.",
              });
            }
          } catch {
            notify.error({
              title: "Paste Error",
              children: "Failed to load sample types for this project.",
            });
            return;
          }
        }

        // merge
        previousConfig.activities.push(
          ...activities.map((activity) => {
            return {
              ...activity,
              identifier: nodeMap.get(activity.identifier)!,
              metadata: {
                width: activity.metadata.width,
                position: {
                  x: activity.metadata.position.x - currentViewport!.x,
                  y: activity.metadata.position.y - currentViewport!.y,
                },
              },
              parameters: activity.parameters
                .map((parameter) => {
                  if (parameter.typeName === "SampleParam" || parameter.typeName === "TrackerParam") {
                    // set above
                    if (currentSaveData.config.sampleTypeId === sampleTypeId) {
                      // sample type of copied activity is the same as the sample type used in the target session
                      return parameter;
                    }

                    return {
                      ...parameter,
                      values: null,
                    };
                  }

                  return parameter;
                })
                .map((p) => ({ ...p, typeName: undefined })),
            };
          }),
        );

        previousConfig.connectors.push(
          ...connectors.map((connector) => ({
            ...connector,
            source: nodeMap.get(connector.source)!,
            target: nodeMap.get(connector.target)!,
          })),
        );

        // Save and Reload!
        set({ isSavingWorkflow: true });
        try {
          await save({ ...currentSaveData, config: previousConfig }, set);
          await get().loadWorkflowEvent(get().workflowEvent.id, true);
        } catch {
          notify.error({
            title: "An error occurred during paste",
            children: "We were unable to save or reload the current session.",
          });
        }
      }

      // If it's all good delete token and reset to avoid side effects from excessive pasting
      set(initialCopyPasteSliceState);
      delete storage[LOCAL_STORAGE_KEY];
    },
  };
};
